import { useEffect, useState } from 'react'
import type { LimitedOfferPrices } from '@/scenes/Editor/components/Navbar/components/SignInManager'
import { formatPlanPrice } from '@/scenes/Editor/components/Navbar/components/SignIn/Metsikon/fallbackPlanDetail'
import { getReadyLimitedOfferPrices, waitForLimitedOfferPrices } from './limitedOfferPrices'

export interface LimitedOfferDisplayPrices {
  /** Regular yearly price per month, shown struck through; `null` without a discount. */
  listPerMonth: string | null
  /** The price per month the user pays. */
  offerPerMonth: string
  /** The yearly total the user is billed. */
  billedYearly: string
}

function toDisplay(prices: LimitedOfferPrices | null): LimitedOfferDisplayPrices | null {
  if (!prices) return null
  const { regular, discounted } = prices
  const pay = discounted ?? regular
  const perMonth = (amount: number) => formatPlanPrice(amount / 12, pay.currencyCode, pay.countryCode)
  return {
    listPerMonth: discounted ? perMonth(regular.amount) : null,
    offerPerMonth: perMonth(pay.amount),
    billedYearly: pay.display,
  }
}

/**
 * Localized prices of the offer's plan (yearly Premium without trial). The
 * offer opens only once they are fetched, so they're normally there on the
 * first frame; `null` means not loaded yet, and the modal shows no price.
 */
export function useLimitedOfferPrices(active: boolean): LimitedOfferDisplayPrices | null {
  const [prices, setPrices] = useState(() => toDisplay(getReadyLimitedOfferPrices()))

  useEffect(() => {
    if (!active) return
    let cancelled = false
    const ready = getReadyLimitedOfferPrices()
    if (ready) {
      setPrices(toDisplay(ready))
      return
    }
    void waitForLimitedOfferPrices(null).then(loaded => {
      if (!cancelled && loaded) setPrices(toDisplay(loaded))
    })
    return () => { cancelled = true }
  }, [active])

  return prices
}
